import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";

const inputStyle = {
  width: "100%", padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1",
  fontSize: 12.5, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};
const th = { padding: "9px 12px", fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", textAlign: "left" };
const td = { padding: "6px 12px", verticalAlign: "middle" };

/**
 * CRUD sederhana untuk tabel referensi (categories/brands/types):
 * kolom `id` (slug, tidak bisa diubah setelah dibuat), `label`, opsional
 * `extraFields` (mis. warna), dan `sort_order`.
 *
 * Props:
 * - tableName: nama tabel di Supabase
 * - title: label tampilan (mis. "Kategori")
 * - extraFields: [{ key, label, type: "text" | "color" }]
 * - idHint: placeholder untuk kolom ID pada form tambah baru
 */
export default function CatalogManager({ tableName, title, extraFields = [], idHint }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);

  const [newRow, setNewRow] = useState(() => makeEmptyRow(extraFields));
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase.from(tableName).select("*").order("sort_order", { ascending: true });
    if (err) setError(err.message);
    else setRows(data || []);
    setLoading(false);
  }, [tableName]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateLocalRow = (id, key, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  const saveRow = async (row) => {
    setSavingId(row.id);
    setError("");
    const { id, ...rest } = row;
    const { error: err } = await supabase.from(tableName).update(rest).eq("id", id);
    setSavingId(null);
    if (err) setError(err.message);
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Hapus "${row.label}"? Produk yang masih memakai ID ini bisa gagal tampil.`)) return;
    setError("");
    const { error: err } = await supabase.from(tableName).delete().eq("id", row.id);
    if (err) {
      setError(
        err.message.includes("foreign key")
          ? `Tidak bisa dihapus -- masih dipakai oleh produk yang ada. Ganti dulu produk yang memakainya, baru hapus.`
          : err.message
      );
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    if (!newRow.id.trim() || !newRow.label.trim()) {
      setError("ID dan Label wajib diisi.");
      return;
    }
    setAdding(true);
    const payload = {
      ...newRow,
      id: newRow.id.trim().toLowerCase().replace(/\s+/g, "-"),
      label: newRow.label.trim(),
      sort_order: Number(newRow.sort_order) || 0,
    };
    const { error: err } = await supabase.from(tableName).insert(payload);
    setAdding(false);
    if (err) {
      setError(err.message);
      return;
    }
    setNewRow(makeEmptyRow(extraFields));
    refresh();
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 14 }}>
        Perubahan di sini langsung memengaruhi pilihan {title.toLowerCase()} di form tambah/edit produk.
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 12, padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Memuat...</div>
      ) : (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={th}>ID</th>
                <th style={th}>Label</th>
                {extraFields.map((f) => <th key={f.key} style={th}>{f.label}</th>)}
                <th style={th}>Urutan</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4 + extraFields.length} style={{ ...td, textAlign: "center", color: "#94a3b8", padding: 18 }}>Belum ada data.</td></tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 11.5, color: "#64748b" }}>{row.id}</td>
                  <td style={td}>
                    <input
                      value={row.label || ""}
                      onChange={(e) => updateLocalRow(row.id, "label", e.target.value)}
                      onBlur={() => saveRow(row)}
                      style={inputStyle}
                    />
                  </td>
                  {extraFields.map((f) => (
                    <td key={f.key} style={td}>
                      {f.type === "color" ? (
                        <input
                          type="color"
                          value={row[f.key] || "#3b82f6"}
                          onChange={(e) => updateLocalRow(row.id, f.key, e.target.value)}
                          onBlur={() => saveRow(row)}
                          style={{ width: 44, height: 30, padding: 2, border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer" }}
                        />
                      ) : (
                        <input
                          value={row[f.key] || ""}
                          onChange={(e) => updateLocalRow(row.id, f.key, e.target.value)}
                          onBlur={() => saveRow(row)}
                          style={inputStyle}
                        />
                      )}
                    </td>
                  ))}
                  <td style={{ ...td, width: 70 }}>
                    <input
                      type="number"
                      value={row.sort_order ?? 0}
                      onChange={(e) => updateLocalRow(row.id, "sort_order", Number(e.target.value))}
                      onBlur={() => saveRow(row)}
                      style={{ ...inputStyle, width: 60 }}
                    />
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {savingId === row.id && <span style={{ fontSize: 10.5, color: "#94a3b8", marginRight: 8 }}>menyimpan...</span>}
                    <button
                      onClick={() => deleteRow(row)}
                      style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 10, padding: 12 }}>
        <div style={{ flex: "0 0 140px" }}>
          <label style={miniLabel}>ID (slug)</label>
          <input value={newRow.id} onChange={(e) => setNewRow({ ...newRow, id: e.target.value })} placeholder={idHint || "contoh: proyektor"} style={inputStyle} />
        </div>
        <div style={{ flex: "1 1 160px" }}>
          <label style={miniLabel}>Label</label>
          <input value={newRow.label} onChange={(e) => setNewRow({ ...newRow, label: e.target.value })} placeholder="Nama tampilan" style={inputStyle} />
        </div>
        {extraFields.map((f) => (
          <div key={f.key} style={{ flex: "0 0 auto" }}>
            <label style={miniLabel}>{f.label}</label>
            {f.type === "color" ? (
              <input type="color" value={newRow[f.key] || "#3b82f6"} onChange={(e) => setNewRow({ ...newRow, [f.key]: e.target.value })} style={{ width: 44, height: 32, padding: 2, border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer" }} />
            ) : (
              <input value={newRow[f.key] || ""} onChange={(e) => setNewRow({ ...newRow, [f.key]: e.target.value })} style={inputStyle} />
            )}
          </div>
        ))}
        <div style={{ flex: "0 0 80px" }}>
          <label style={miniLabel}>Urutan</label>
          <input type="number" value={newRow.sort_order} onChange={(e) => setNewRow({ ...newRow, sort_order: e.target.value })} style={inputStyle} />
        </div>
        <button
          type="submit"
          disabled={adding}
          style={{ padding: "8px 16px", borderRadius: 7, border: "none", background: "#3b82f6", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer", opacity: adding ? 0.6 : 1 }}
        >
          {adding ? "Menambah..." : `+ Tambah ${title}`}
        </button>
      </form>
    </div>
  );
}

function makeEmptyRow(extraFields) {
  const row = { id: "", label: "", sort_order: 0 };
  extraFields.forEach((f) => { row[f.key] = f.type === "color" ? "#3b82f6" : ""; });
  return row;
}

const miniLabel = { fontSize: 10, fontWeight: 700, color: "#94a3b8", display: "block", marginBottom: 3, textTransform: "uppercase" };
